import { doc, runTransaction, serverTimestamp, Timestamp } from 'firebase/firestore';

import { firestore } from './config';
import { USE_API, api } from '@/services/api/client';
import { areaNombre, type AreaId } from '@/utils/areas';

export type ColeccionStock = 'productos_venta' | 'insumos_internos';

export interface RegistrarSalidaInput {
  productoId: string;
  coleccion: ColeccionStock;
  cantidad: number;
  destino: AreaId;
  motivo?: string;
  autorUid: string;
  autorNombre: string;
}

export async function registrarSalida(input: RegistrarSalidaInput) {
  if (USE_API) {
    await api.post('/acciones/salida', {
      productoId: input.productoId,
      coleccion: input.coleccion,
      cantidad: input.cantidad,
      destino: input.destino,
      destinoNombre: areaNombre(input.destino),
      motivo: input.motivo ?? null,
      autorUid: input.autorUid,
      autorNombre: input.autorNombre,
    });
    return;
  }
  await runTransaction(firestore, async (tx) => {
    const productoRef = doc(firestore, input.coleccion, input.productoId);
    const snap = await tx.get(productoRef);
    if (!snap.exists()) throw new Error('El producto ya no existe.');
    const p = snap.data() as any;
    if ((p.stock ?? 0) < input.cantidad) {
      throw new Error(`No hay suficiente stock. Quedan ${p.stock ?? 0}.`);
    }
    const nuevoStock = (p.stock ?? 0) - input.cantidad;
    const stockMin = p.stockMin ?? 0;
    tx.update(productoRef, {
      stock: nuevoStock,
      stockBajo: nuevoStock <= stockMin,
      updatedAt: serverTimestamp(),
    });
    const salidaRef = doc(
      firestore,
      'salidas_almacen',
      `salida-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    );
    tx.set(salidaRef, {
      productoId: input.productoId,
      productoNombre: p.nombre,
      productoPresentacion: p.presentacion ?? null,
      coleccion: input.coleccion,
      cantidad: input.cantidad,
      destino: input.destino,
      destinoNombre: areaNombre(input.destino),
      motivo: input.motivo ?? null,
      fecha: Timestamp.now(),
      autorUid: input.autorUid,
      autorNombre: input.autorNombre,
      createdAt: serverTimestamp(),
    });
  });
}

/** @deprecated Usar registrarSalida. */
export const venderProducto = registrarSalida;

export interface IngresarStockInput {
  productoId: string;
  coleccion: 'productos_venta' | 'insumos_internos';
  cantidad: number;
  costoTotal?: number;
  proveedorId?: string | null;
  proveedorNombre?: string | null;
  vencimiento?: Date | null;
  notas?: string;
  autorUid: string;
  autorNombre: string;
  registrarEgreso: boolean;
}

export async function ingresarStock(input: IngresarStockInput) {
  if (USE_API) {
    await api.post('/acciones/ingresar-stock', {
      productoId: input.productoId,
      coleccion: input.coleccion,
      cantidad: input.cantidad,
      costoTotal: input.costoTotal ?? 0,
      proveedorNombre: input.proveedorNombre ?? null,
      vencimiento: input.vencimiento ? input.vencimiento.toISOString().slice(0, 10) : null,
      autorUid: input.autorUid,
      autorNombre: input.autorNombre,
      registrarEgreso: input.registrarEgreso,
    });
    return;
  }
  await runTransaction(firestore, async (tx) => {
    const ref = doc(firestore, input.coleccion, input.productoId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('El producto ya no existe.');
    const p = snap.data() as any;
    const nuevoStock = (p.stock ?? 0) + input.cantidad;
    const stockMin = p.stockMin ?? 0;
    const update: any = {
      stock: nuevoStock,
      stockBajo: nuevoStock <= stockMin,
      updatedAt: serverTimestamp(),
    };
    if (input.vencimiento) update.vencimiento = Timestamp.fromDate(input.vencimiento);
    tx.update(ref, update);
    if (input.registrarEgreso && input.costoTotal && input.costoTotal > 0) {
      const movRef = doc(firestore, 'movimientos', `compra-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
      tx.set(movRef, {
        bloque: 'clinica',
        tipo: 'egreso',
        categoria: 'Insumos',
        monto: input.costoTotal,
        metodoPago: 'efectivo',
        descripcion: `Reposición ${input.cantidad} × ${p.nombre} (${p.presentacion})`,
        beneficiario: input.proveedorNombre ?? null,
        adjuntoUrl: null,
        fecha: Timestamp.now(),
        autorUid: input.autorUid,
        autorNombre: input.autorNombre,
        productoId: input.productoId,
        cantidad: input.cantidad,
        createdAt: serverTimestamp(),
      });
    }
  });
}

export async function usarInsumo(input: {
  insumoId: string;
  cantidad: number;
  motivo?: string;
  destino?: string;
  destinoNombre?: string;
  autorUid: string;
  autorNombre: string;
}) {
  const destino = input.destino ?? 'uso_interno';
  const destinoNombre = input.destinoNombre ?? 'Uso interno';
  if (USE_API) {
    await api.post('/acciones/usar-insumo', {
      insumoId: input.insumoId,
      cantidad: input.cantidad,
      destino,
      destinoNombre,
      motivo: input.motivo ?? null,
      autorUid: input.autorUid,
      autorNombre: input.autorNombre,
    });
    return;
  }
  await runTransaction(firestore, async (tx) => {
    const ref = doc(firestore, 'insumos_internos', input.insumoId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('El insumo ya no existe.');
    const p = snap.data() as any;
    if ((p.stock ?? 0) < input.cantidad) {
      throw new Error(`Stock insuficiente. Quedan ${p.stock ?? 0}.`);
    }
    const nuevoStock = (p.stock ?? 0) - input.cantidad;
    tx.update(ref, {
      stock: nuevoStock,
      stockBajo: nuevoStock <= (p.stockMin ?? 0),
      updatedAt: serverTimestamp(),
    });
    // Registramos la salida para que quede en el historial.
    const salidaRef = doc(
      firestore,
      'salidas_almacen',
      `uso-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    );
    tx.set(salidaRef, {
      productoId: input.insumoId,
      productoNombre: p.nombre,
      productoPresentacion: p.presentacion ?? null,
      coleccion: 'insumos_internos',
      cantidad: input.cantidad,
      destino,
      destinoNombre,
      motivo: input.motivo ?? null,
      fecha: Timestamp.now(),
      autorUid: input.autorUid,
      autorNombre: input.autorNombre,
      createdAt: serverTimestamp(),
    });
  });
}
